 
import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import {
    Dialog,
    DialogContent
} from '@/components/ui/dialog' 
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query' 
import { isAxiosError } from '@/api/business'
import { useEffect, useMemo, useState } from 'react'   
import DefaultColumn from '@/components/Table/components/DefaultColumn'
import Button from "@/components/Button" 
import { FilePen, FilterX, RefreshCcw  } from 'lucide-react'
import { TypographyH4 } from '@/components/ui/typography' 
import { useTable } from '@/hooks/useTable'
import { createColumnHelper } from '@tanstack/react-table'  
import ButtonWithTooltip from '@/components/ButtonWithTooltip'
import { useProductQuery } from '@/queries/useProductQuery'
import { Product } from '@/types/Product'
import { createProductInMarketResearch } from '@/api/business/marketResearch'
import { ProductInMarketResearchKeys } from '@/queries/useProductInMarketResearchQuery'

export type UnsettledSecuritiesContractControldTable = TableData<Product>

const columnHelper = createColumnHelper<Product>()

interface DialogProps {
    isOpen: boolean
    onClose: () => void 
    ids: number
}

const AddProductInMarketResearchDialog = ({ isOpen, onClose, ids}: DialogProps) => {
    const {
        table,  
        setTable,
        selectedRows,
        setSelectedRows,
        
    } = useTable<Product>('')  

    const queryClient = useQueryClient()
    const { toast } = useToast() 
    const [productSelected, setProductSelected] = useState<string[]>([])
    const { data, isFetching, isPending, refetch } = useProductQuery()
    
   useEffect(() => {
        refetch()
   }, [ids])

    useEffect(() => {
        const ids = table ? table.getCoreRowModel()
              .flatRows.filter(
                  (row) => selectedRows[row.id]
              )
              .map((row) => row.original.SK_PRODUTO) : []

        setProductSelected(ids)
    }, [selectedRows])
    
    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [
             columnHelper.accessor('SK_PRODUTO', {
                id: 'SK_PRODUTO',
                header: 'Cod. Produto',
                size: 130,
            }), 
            columnHelper.accessor('DS_PRODUTO', {
                id: 'DS_PRODUTO',
                header: 'Produto',
                size: 440,
            }), 
            columnHelper.accessor('DD_SKU', {
                id: 'DD_SKU',
                header: 'Embalagem',
                size: 100,
            }), 
            columnHelper.accessor('DD_UNIDADE_VENDA', {
                id: 'DD_UNIDADE_MEDIDA',
                header: 'Uni. Venda',
                size: 120,
            }),  
            columnHelper.accessor('DD_MARCA', {
                id: 'DD_MARCA',
                header: 'Marca',
                size: 180,
            }),  
            columnHelper.accessor('DS_DETENTOR', {
                id: 'DS_DETENTOR',
                header: 'Fabricante',
                size: 120,
            }),  
            columnHelper.accessor('DD_ORIGEM_DADO', {
                id: 'DD_ORIGEM_DADO',
                header: 'Cadastro',
                size: 120,
            }) 
        ],
        []
    )

    const { mutate } = useMutation({
        mutationFn: createProductInMarketResearch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ProductInMarketResearchKeys.details() })
            setSelectedRows({}) 
            toast({
                title: 'Produtos adicionados na pesquisa com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível adicionar os produtos. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao adicionar os produtos',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })
          
    const onSubmit = () => {  
        mutate({
            SK_PESQUISA: ids,
            SK_PRODUTO: productSelected
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[80%] h-3/4'>  
                <Table<UnsettledSecuritiesContractControldTable>
                        data={memoData}
                        columns={memoColumns}
                        isLoading={isPending}
                        isFetching={isFetching} 
                        expandAllRows={true}   
                        enableRowSelection
                        tableState={{
                            rowSelection: selectedRows,
                        }}
                        onRowSelectionChange={setSelectedRows}
                        tableHeader={(
                            <TypographyH4>Produtos disponíveis</TypographyH4>
                        )}  
                        tableActions={(
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 items-center h-full">
                                {productSelected.length > 0 && (
                                    <Button
                                        className="flex items-center  gap-4 h-[40px] justify-start text-sm hover:text-primary-500 hover:bg-accent"
                                        variant={'ghost'}
                                        onClick={() => table && onSubmit()}
                                    >
                                        <FilePen size={14} />
                                        Adicionar Produtos
                                    </Button>  
                                )}
                                <Button
                                    className="flex items-center  gap-4 h-[40px] justify-start text-sm hover:text-primary-500 hover:bg-accent"
                                    variant={'ghost'}
                                    onClick={() => table && table.resetColumnFilters()}
                                >
                                    <FilterX size={14} />
                                    Remover Todos os Filtros
                                </Button> 
                                <ButtonWithTooltip
                                    tooltipContent={<p className="text-white">Atualizar</p>}
                                    onClick={() => refetch()}
                                    variant="ghost"
                                >
                                    <RefreshCcw size={18} />
                                </ButtonWithTooltip>  
                            </div>
                        )}     
                        getTableInstance={(table) => { 
                            setTable(table) 
                        }} 
                        defaultColumn={{
                            cell: ({ getValue }) => (
                                <DefaultColumn>{getValue() as string}</DefaultColumn>
                            ),
                        }}
                    />
            </DialogContent>
        </Dialog>
    )
}
    
export default AddProductInMarketResearchDialog
